import { TabData } from '@ddt-web/data-service';
import { ObjectUtil, StringUtil } from '@ddt-web/generic-utils';
import * as get from 'lodash/get';

import { ChartFactory } from '../factories';
import { ScatterChartSeriesCustomisations } from '../models';
import { ChartConfigHelper } from './config.helper';
import { DefaultChartConfig } from './default-chart.config';
import { ChartType } from './enums/chart-type.enum';

declare const Highcharts;

const DEFAULT_SCATTER_CHART_CONFIG: any = {
	plotOptions: {
		series: {
			marker: {
				radius: 8,
				symbol: 'circle'
			}
		}
	},
	legend: {
		enabled: true,
		align: 'left',
		verticalAlign: 'top',
		layout: 'vertical',
		itemMarginTop: 5,
		itemMarginBottom: 5,
		width: 230,
		useHTML: true,
		labelFormatter: function (): string {
			const parts = StringUtil.splitStringByPattern(this.name, 25, 999);
			return StringUtil.joinStringPartsForChartTitle(parts, 25, 10, true);
		}
	}
};

export class ScatterChartConfig extends DefaultChartConfig {
	private seriesColors: Array<string> = [];
	private seriesData: Array<any> = [];
	private seriesCategories: Array<any> = [];

	constructor(
		protected type: ChartType,
		protected name: string,
		protected container: HTMLElement,
		protected data: TabData | Array<TabData>,
		protected dataIndex?: number,
		protected colors?: Array<string>,
		protected height?: number,
		protected width?: number,
		protected title?: string,
		protected hideTitle?: boolean,
		protected x?: string,
		protected y?: string,
		protected customAttributes?: ScatterChartCustomAttributes
	) {
		super(type, container, colors, height, width, title, hideTitle);
		this.initialize();
	}

	private initialize(): void {
		this.configuration = ObjectUtil.extend(this.configuration, DEFAULT_SCATTER_CHART_CONFIG);
		const data = [].concat(this.data);
		this.seriesColors = this.configuration.colors || ChartFactory.getPortfolioColors();

		this.seriesData = ChartFactory.getDefaultScatterSeriesForTabData(data, this.customAttributes?.multiSeriesConfig);

		this.seriesCategories = ChartFactory.getCategoriesForTabData(data[0]);
		this.updateDefaultConfiguration();

		if (this.customAttributes) {
			const { allSeriesData, axisYMin, multiSeriesConfig, plotLineSeries, auxiliaryData, ...validHighChartsAttributes } = this.customAttributes;

			if (auxiliaryData) {
				this.configuration.series = ChartFactory.enrichSeriesWithAuxiliaryData(this.seriesData, auxiliaryData);
			}

			if (plotLineSeries) {
				this.configuration.series.push(this.customAttributes.plotLineSeries);
			}

			this.configuration = ChartConfigHelper.applyCustomAttributesToConfiguration(validHighChartsAttributes, this.configuration);
		}
	}

	public updateDefaultConfiguration(): void {
		const categories = get(this.customAttributes, 'seriesCategories', this.seriesCategories);
		const seriesDataToCalculateAxis = (get(this.customAttributes, 'allSeriesData', null) ? this.customAttributes.allSeriesData : this.seriesData).filter(series => series);
		const autoAxis = get(this.customAttributes, 'autoAxis', false);
		const tooltipWidth = get(this.customAttributes, 'tooltipWidth', 190);
		const ticksX = Math.floor(Math.max(...seriesDataToCalculateAxis.map(point => (isNaN(point.data?.[0]?.[0]) ? 0 : point.data[0][0]))) / 5) + 4;
		const ticksY = Math.floor(Math.max(...seriesDataToCalculateAxis.map(point => (isNaN(point.data?.[0]?.[1]) ? 0 : point.data[0][1]))) / 5) + 4;
		this.configuration = ObjectUtil.extend(this.configuration, {
			colors: this.seriesColors,
			series: this.seriesData.filter(series => series),
			chart: {
				marginTop: 20
			},
			xAxis: {
				title: {
					text: categories[1] + ' %',
					enabled: true,
					style: {
						color: '#A5A5A5'
					}
				},
				tickInterval: autoAxis ? undefined : 5,
				tickAmount: autoAxis ? undefined : ticksX,
				min: autoAxis ? undefined : 0
			},
			yAxis: {
				title: {
					text: categories[0] + ' %',
					enabled: true,
					style: {
						color: '#A5A5A5'
					}
				},
				tickInterval: autoAxis ? undefined : 5,
				tickAmount: autoAxis ? undefined : ticksY,
				min: autoAxis ? undefined : (get(this.customAttributes, 'axisYMin', null) ? this.customAttributes.axisYMin : 0)
			},
			tooltip: {
				backgroundColor: 'rgba(255, 255, 255, 0.9)',
				borderWidth: 0,
				useHTML: true,
				hideDelay: 50,
				padding: 0,
				style: {
					fontWeight: 'light'
				},
				formatter: function (): string {
					return (
						`<div style="width: ${tooltipWidth}px; white-space:normal; background-color:rgba(255, 255, 255, 0.9);padding:15px;border:1px solid #a5a5a5">` +
						'<table style="table-layout:fixed">' +
						'<th><b>' +
						StringUtil.escapeHtmlWithChartLineBreaks(StringUtil.splitSpaceLessStringForChart(this.point.name || this.series.name, 17, true)) +
						'</b></th>' +
						'<tr>' +
						'<td align="left">' +
						StringUtil.escapeHtml(categories[0]) +
						'</td>' +
						'<td align="right"> ' +
						Highcharts.numberFormat(this.point.y, 2, '.', ',') +
						'%</td>' +
						'</tr>' +
						'<tr>' +
						'<td align="left">' +
						StringUtil.escapeHtml(categories[1]) +
						'</td>' +
						'<td align="right"> ' +
						Highcharts.numberFormat(this.point.x, 2, '.', ',') +
						'%</td>' +
						'</tr>' +
						(this.point.toolTipAdditionalData ? this.point.toolTipAdditionalData : '') +
						'</table>' +
						'</div>'
					);
				}
			}
		});
		return this.configuration;
	}

	public getConfigurationForPDF(customAttributes: any = {}): any {
		const config = this.updateDefaultConfigurationForPdf();

		return ChartConfigHelper.applyCustomAttributesToConfiguration(customAttributes, config);
	}

	public render(): void {
		Highcharts.chart(this.configuration);
	}

	private updateDefaultConfigurationForPdf(): any {
		return ObjectUtil.extend(this.configuration, {
			chart: {
				marginTop: 7
			},
			legend: {
				enabled: false
			},
			plotOptions: {
				series: {
					marker: {
						radius: 4
					}
				}
			},
			xAxis: {
				lineWidth: 0.25,
				lineColor: '#000',
				title: {
					style: {
						color: '#000',
						fontSize: '8px'
					}
				},
				labels: {
					style: {
						fontSize: '8px'
					}
				},
				tickWidth: 0.25,
				tickColor: '#000'
			},
			yAxis: {
				gridLineWidth: 0.25,
				gridLineColor: '#616161',
				title: {
					style: {
						color: '#000',
						fontSize: '8px'
					}
				},
				labels: {
					style: {
						fontSize: '8px'
					},
					y: 3
				}
			}
		});
	}
}

export interface ScatterChartCustomAttributes {
	allSeriesData: any[];
	axisYMin?: number;
	plotLineSeries?: any;
	multiSeriesConfig?: ScatterChartSeriesCustomisations[];
	// used to add series related miscellaneous data to a series e.g. series specific tooltip data points that are not displayed on the chart
	auxiliaryData?: any[];
	// used this to override all the series data generated from default scatter chart
	series?: any;
}
